import axios from 'axios';
import { participantApiBase } from '../utils/sandboxEndpoints';

// Create axios instance with default config
const api = axios.create({
  baseURL: '/iso20022/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Clears the local session and bounces to the login page when the backend
// rejects our session token (401). Guards against a redirect loop on the
// login pages themselves.
function handleSessionExpiry() {
  const onAuthPage = window.location.pathname.startsWith('/login')
                  || window.location.pathname.startsWith('/platform/login');
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('tenantId');
  if (!onAuthPage) {
    window.location.href = '/login';
  }
}

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`);
    // Same session token as the FSPIOP corridor — the ISO engine verifies it
    // with the shared SESSION_JWT_SECRET and resolves the tenant from the claim.
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.config.url}`, response.status);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      handleSessionExpiry();
    }
    console.error('[API Response Error]', error.response || error);
    return Promise.reject(error);
  }
);

/**
 * Dashboard API Service
 */
export const dashboardApi = {
  /**
   * Get comprehensive statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  /**
   * Get statistics by status
   */
  getStatsByStatus: async () => {
    const response = await api.get('/admin/stats/by-status');
    return response.data;
  },

  /**
   * Get volume statistics
   * @param {string} period - Time period (hourly, daily, weekly)
   * @param {number} limit - Number of data points
   */
  getVolumeStats: async (period = 'hourly', limit = 24) => {
    const response = await api.get('/admin/stats/volume', {
      params: { period, limit }
    });
    return response.data;
  },

  /**
   * Get filtered transactions
   * @param {Object} filters - Filter parameters
   */
  getTransactions: async (filters = {}) => {
    const response = await api.get('/admin/transactions', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get recent transactions
   * @param {number} limit - Number of transactions
   */
  getRecentTransactions: async (limit = 10) => {
    const response = await api.get('/admin/transactions/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get transaction by ID
   * @param {number} id - Transaction ID
   */
  getTransactionById: async (id) => {
    const response = await api.get(`/admin/transactions/${id}`);
    return response.data;
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    const response = await api.get('/admin/health');
    return response.data;
  },

  // =========================================================================
  // Request-to-Pay (pain.013) API Methods
  // =========================================================================

  /**
   * Get pain.013 RtP statistics
   * @returns {Promise<Object>} RtP statistics
   */
  getRtpStats: async () => {
    const response = await api.get('/admin/rtp/stats');
    return response.data;
  },

  /**
   * Get list of RtP transactions with filtering
   * @param {Object} filters - Filter parameters
   * @param {number} filters.limit - Maximum results (default: 100)
   * @param {string} filters.status - Status filter (PENDING, APPROVED, REJECTED, etc.)
   * @param {string} filters.direction - Direction filter (OUTGOING, INCOMING)
   * @param {string} filters.startDate - Start date (YYYY-MM-DD)
   * @param {string} filters.endDate - End date (YYYY-MM-DD)
   * @param {string} filters.search - Search term (msgId, endToEndId, party names)
   * @returns {Promise<Array>} List of RtP transactions
   */
  getRtpTransactions: async (filters = {}) => {
    const params = {};
    
    if (filters.limit) params.limit = filters.limit;
    if (filters.status) params.status = filters.status;
    if (filters.direction) params.direction = filters.direction;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;
    
    const response = await api.get('/admin/rtp/transactions', { params });
    return response.data;
  },

  /**
   * Get a specific RtP transaction by ID
   * @param {number} id - RtP transaction ID
   * @returns {Promise<Object>} RtP transaction details
   */
  getRtpTransactionById: async (id) => {
    const response = await api.get(`/admin/rtp/transactions/${id}`);
    return response.data;
  },

  /**
   * Get related messages for an RtP transaction
   * Returns linked pain.013, pacs.008, and pacs.002 messages by EndToEndId
   * @param {number} id - RtP transaction ID
   * @returns {Promise<Object>} Related messages (rtpRequest, payment, statusUpdates)
   */
  getRtpRelatedMessages: async (id) => {
    try {
      const response = await api.get(`/admin/rtp/transactions/${id}/related`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { rtpRequest: null, payment: null, statusUpdates: [] };
      }
      throw error;
    }
  },

  /**
   * Get RtP status breakdown
   * @returns {Promise<Object>} Status counts
   */
  getRtpStatusBreakdown: async () => {
    const response = await api.get('/admin/rtp/status-breakdown');
    return response.data;
  },

  /**
   * Get RtP volume over time
   * @param {string} period - Time period (hour, day, week, month)
   * @param {number} days - Number of days to look back (optional)
   * @returns {Promise<Array>} Time-series data
   */
  getRtpVolume: async (period = 'day', days = null) => {
    const params = { period };
    if (days) params.days = days;
    
    const response = await api.get('/admin/rtp/volume', { params });
    return response.data;
  },

  /**
   * Search RtP transactions by EndToEndId
   * @param {string} endToEndId - End-to-End ID to search
   * @returns {Promise<Array>} Matching RtP transactions
   */
  searchRtpByEndToEndId: async (endToEndId) => {
    const response = await api.get(`/admin/rtp/search/e2e/${encodeURIComponent(endToEndId)}`);
    return response.data;
  },

  /**
   * Get RtP transactions requiring attention
   * Returns transactions that need action:
   * - Pending approvals >24 hours old
   * - Failed requests in last 7 days
   * @returns {Promise<Array>} RtP transactions requiring attention
   */
  getRtpAttentionRequired: async () => {
    const response = await api.get('/admin/rtp/attention-required');
    return response.data;
  },

  // =========================================================================
  // Request-to-Pay Status/Response (pain.014) API
  // =========================================================================

  /**
   * Get pain.014 RTP Status statistics for dashboard
   * @returns {Promise<Object>} RTP Status statistics
   */
  async getRtpStatusStats() {
    const response = await api.get('/admin/rtp-status/stats');
    return response.data;
  },

  /**
   * Get list of pain.014 RTP Status transactions with filtering
   * @param {Object} params - Query parameters (limit, grpSts, txSts, direction, startDate, endDate, search)
   * @returns {Promise<Array>} List of RTP Status transactions
   */
  async getRtpStatusTransactions(params = {}) {
    const response = await api.get('/admin/rtp-status/transactions', { params });
    return response.data;
  },

  /**
   * Get a specific pain.014 RTP Status transaction by ID
   * @param {number} id - RTP Status transaction ID
   * @returns {Promise<Object>} RTP Status transaction details
   */
  async getRtpStatusTransactionById(id) {
    const response = await api.get(`/admin/rtp-status/transactions/${id}`);
    return response.data;
  },

  /**
   * Get related messages for a pain.014 RTP Status transaction
   * @param {number} id - RTP Status transaction ID
   * @returns {Promise<Object>} Related messages (rtpStatus, rtpRequest, payment, paymentStatuses)
   */
  async getRtpStatusRelatedMessages(id) {
    try {
      const response = await api.get(`/admin/rtp-status/transactions/${id}/related`);
      return response.data;
    } catch (error) {
      console.error('Error fetching RTP Status related messages:', error);
      return { rtpStatus: null, rtpRequest: null, payment: null, paymentStatuses: [] };
    }
  },

  /**
   * Get RTP Status breakdown by group status code
   * @returns {Promise<Object>} Status code counts
   */
  async getRtpStatusBreakdown() {
    const response = await api.get('/admin/rtp-status/status-breakdown');
    return response.data;
  },

  /**
   * Get RTP Status volume over time
   * @param {Object} params - Query parameters (period, limit)
   * @returns {Promise<Array>} Time-series volume data
   */
  async getRtpStatusVolume(params = {}) {
    const response = await api.get('/admin/rtp-status/volume', { params });
    return response.data;
  },

  /**
   * Search RTP Status messages by original EndToEndId
   * @param {string} endToEndId - Original End-to-End ID
   * @returns {Promise<Array>} List of matching RTP Status transactions
   */
  async searchRtpStatusByEndToEndId(endToEndId) {
    const response = await api.get(`/admin/rtp-status/search/e2e/${encodeURIComponent(endToEndId)}`);
    return response.data;
  },

  /**
   * Get RTP Status messages requiring attention
   * @param {number} limit - Maximum results (default 50)
   * @returns {Promise<Array>} List of RTP Status transactions needing attention
   */
  async getRtpStatusAttentionRequired(limit = 50) {
    const response = await api.get('/admin/rtp-status/attention-required', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get pacs.028 status request statistics
   * @returns {Promise<Object>} Status request statistics
   */
  getStatusRequestStats: async () => {
    const response = await api.get('/admin/status-requests/stats');
    return response.data;
  },

  /**
   * Get list of status request messages with filtering
   * @param {Object} filters - Filter parameters
   * @param {number} filters.limit - Maximum results (default: 100)
   * @param {string} filters.status - Status filter (SIGNED, SENT, RECEIVED, PROCESSING, FAILED)
   * @param {string} filters.direction - Direction filter (OUTGOING_SIGNED, INCOMING)
   * @param {string} filters.startDate - Start date (YYYY-MM-DD)
   * @param {string} filters.endDate - End date (YYYY-MM-DD)
   * @param {string} filters.search - Search term (msgId, orglEndToEndId, orglTxId, agent names)
   * @returns {Promise<Array>} List of status request messages
   */
  getStatusRequests: async (filters = {}) => {
    const params = {};
    
    if (filters.limit) params.limit = filters.limit;
    if (filters.status) params.status = filters.status;
    if (filters.direction) params.direction = filters.direction;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;
    
    const response = await api.get('/admin/status-requests', { params });
    return response.data;
  },

  /**
   * Get a specific status request by ID
   * @param {number} id - Status request ID
   * @returns {Promise<Object>} Status request details
   */
  getStatusRequestById: async (id) => {
    const response = await api.get(`/admin/status-requests/${id}`);
    return response.data;
  },

  /**
   * Get related messages for a status request
   * Returns linked pacs.028, pacs.008, and pacs.002 messages by orglEndToEndId
   * @param {number} id - Status request ID
   * @returns {Promise<Object>} Related messages (statusRequest, originalPayment, statusResponses)
   */
  getStatusRequestRelatedMessages: async (id) => {
    try {
      const response = await api.get(`/admin/status-requests/${id}/related`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { statusRequest: null, originalPayment: null, statusResponses: [] };
      }
      throw error;
    }
  },

  /**
   * Get status request status breakdown
   * @returns {Promise<Object>} Status counts
   */
  getStatusRequestStatusBreakdown: async () => {
    const response = await api.get('/admin/status-requests/status-breakdown');
    return response.data;
  },

  /**
   * Get status request volume over time
   * @param {string} period - Time period (hour, day, week, month)
   * @param {number} days - Number of days to look back (optional)
   * @returns {Promise<Array>} Time-series data
   */
  getStatusRequestVolume: async (period = 'day', days = null) => {
    const params = { period };
    if (days) params.days = days;
    
    const response = await api.get('/admin/status-requests/volume', { params });
    return response.data;
  },

  /**
   * Search status requests by original end-to-end ID
   * @param {string} orglEndToEndId - Original end-to-end ID to search
   * @returns {Promise<Array>} Matching status requests
   */
  searchStatusRequestsByE2E: async (orglEndToEndId) => {
    const response = await api.get(`/admin/status-requests/search/e2e/${encodeURIComponent(orglEndToEndId)}`);
    return response.data;
  },

  /**
   * Get status requests requiring attention
   * Returns requests that need action:
   * - Requests with status SENT older than 1 hour
   * - Failed requests in last 7 days
   * @returns {Promise<Array>} Status requests requiring attention
   */
  getStatusRequestsAttentionRequired: async () => {
    const response = await api.get('/admin/status-requests/attention-required');
    return response.data;
  }

};

/**
 * Account Registration API Service
 */
export const registrationApi = {
  /**
   * Get registration statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/registrations/stats');
    return response.data;
  },

  /**
   * Get filtered account registrations
   * @param {Object} filters - Filter parameters
   */
  getRegistrations: async (filters = {}) => {
    const response = await api.get('/admin/registrations', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get recent registrations
   * @param {number} limit - Number of registrations
   */
  getRecentRegistrations: async (limit = 10) => {
    const response = await api.get('/admin/registrations/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get registration by ID
   * @param {number} id - Registration ID
   */
  getRegistrationById: async (id) => {
    const response = await api.get(`/admin/registrations/${id}`);
    return response.data;
  }
};

/**
 * Account Modification API Service
 */
export const modificationApi = {
  /**
   * Get modification statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/modifications/stats');
    return response.data;
  },

  /**
   * Get filtered account modifications
   * @param {Object} filters - Filter parameters
   */
  getModifications: async (filters = {}) => {
    const response = await api.get('/admin/modifications', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get recent modifications
   * @param {number} limit - Number of modifications
   */
  getRecentModifications: async (limit = 10) => {
    const response = await api.get('/admin/modifications/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get modification by ID
   * @param {number} id - Modification ID
   */
  getModificationById: async (id) => {
    const response = await api.get(`/admin/modifications/${id}`);
    return response.data;
  }
};

/**
 * Account Closure API Service
 */
export const closureApi = {
  /**
   * Get closure statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/closures/stats');
    return response.data;
  },

  /**
   * Get filtered account closures
   * @param {Object} filters - Filter parameters
   */
  getClosures: async (filters = {}) => {
    const response = await api.get('/admin/closures', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get recent closures
   * @param {number} limit - Number of closures
   */
  getRecentClosures: async (limit = 10) => {
    const response = await api.get('/admin/closures/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get closure by ID
   * @param {number} id - Closure ID
   */
  getClosureById: async (id) => {
    const response = await api.get(`/admin/closures/${id}`);
    return response.data;
  }
};

/**
 * Message Generation API Service
 */
export const messageApi = {
  /**
   * List available schemas
   */
  listSchemas: async () => {
    const response = await api.get('/schemas');
    return response.data;
  },

  /**
   * Generate message
   */
  generateMessage: async (messageType, payload, format = 'xml', pretty = true) => {
    const response = await api.post(`/generate/${messageType}`, payload, {
      params: {
        format,
        pretty
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  /**
   * Generate and sign message in one call
   */
  generateAndSignMessage: async (
    messageType,
    payload,
    format = 'xml',
    pretty = true,
    sign = true,
    forward = false) => {
      const response = await api.post(`/generate/${messageType}`, payload, {
        params: {
          format,
          pretty,
          sign,
          forward
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log(`[API Response] ${response.data}`);
      return response.data;
    },

  /**
   * Validate message
   */
  validateMessage: async (messageType, payload) => {
    const response = await api.post(`/validate/${messageType}`, payload);
    return response.data;
  }
};

/**
 * Sign/Verify API Service
 */
export const signingApi = {
  /**
   * Sign XML message
   */
  signMessage: async (xmlContent, pretty = true) => {
    const response = await api.post('/sign', xmlContent, {
      params: { pretty },
      headers: {
        'Content-Type': 'application/xml'
      }
    });
    return response.data;
  },

  /**
   * Verify signed message
   */
  verifyMessage: async (signedXml) => {
    const response = await api.post('/verify', signedXml, {
      headers: {
        'Content-Type': 'application/xml'
      }
    });
    return response.data;
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    const response = await api.get('/healthz');
    return response.data;
  }
};

/**
 * Certificate API Service
 */
export const certificateApi = {
  /**
   * Get list of certificates with optional filtering
   * @param {Object} filters - Filter parameters
   * @param {string} filters.status - Status filter (ACTIVE, PENDING, REVOKED, EXPIRED)
   * @param {string} filters.type - Type filter (SELF_SIGNED, CA_SIGNED, IMPORTED)
   * @param {string} filters.search - Search term (alias, subject)
   * @returns {Promise<Array>} List of certificates
   */
  getCertificates: async (filters = {}) => {
    const params = {};
    
    if (filters.status) params.status = filters.status;
    if (filters.type) params.certificateType = filters.type;
    if (filters.search) params.search = filters.search;
    
    const response = await api.get('/certificates', { params });
    return response.data;
  },

  /**
   * Get certificate by ID
   * @param {number} id - Certificate ID
   * @returns {Promise<Object>} Certificate details
   */
  getCertificateById: async (id) => {
    const response = await api.get(`/certificates/${id}`);
    return response.data;
  },

  /**
   * Get certificate status summary
   * @returns {Promise<Object>} Status summary (counts, primary cert info, etc.)
   */
  getStatusSummary: async () => {
    const response = await api.get('/certificates/status/summary');
    return response.data;
  },

  /**
   * Get certificates expiring within specified days
   * @param {number} days - Number of days (default: 30)
   * @returns {Promise<Array>} List of expiring certificates
   */
  getExpiringCertificates: async (days = 30) => {
    const response = await api.get('/certificates/expiring', {
      params: { days }
    });
    return response.data;
  },

  /**
   * Get audit log for a certificate
   * @param {number} id - Certificate ID
   * @returns {Promise<Array>} Audit log entries
   */
  getAuditLog: async (id) => {
    const response = await api.get(`/certificates/${id}/audit`);
    return response.data;
  },

  /**
   * Generate self-signed certificate (Model 1)
   * @param {Object} request - Certificate generation request
   * @param {string} request.alias - Certificate alias
   * @param {string} request.commonName - Common Name (CN)
   * @param {string} request.organization - Organization (O)
   * @param {string} request.organizationalUnit - Organizational Unit (OU)
   * @param {string} request.locality - Locality (L)
   * @param {string} request.state - State/Province (ST)
   * @param {string} request.country - Country (C)
   * @param {string} request.keyAlgorithm - Key algorithm (RSA, DSA, EC)
   * @param {number} request.keySize - Key size in bits (2048, 3072, 4096)
   * @param {number} request.validityDays - Validity period in days
   * @returns {Promise<Object>} Generated certificate info
   */
  generateCertificate: async (request) => {
    const response = await api.post('/certificates/generate', request);
    return response.data;
  },

  /**
   * Generate Certificate Signing Request (Model 2 - Step 1)
   * @param {Object} request - CSR generation request (same fields as generateCertificate)
   * @returns {Promise<Object>} CSR generation result with csrPem, csrId, certificateId
   */
  generateCSR: async (request) => {
    const response = await api.post('/certificates/generate-csr', request);
    return response.data;
  },

  /**
   * Import CA-signed certificate (Model 2 - Step 2)
   * @param {number} csrId - CSR ID
   * @param {string} certificatePem - PEM-encoded CA-signed certificate
   * @param {string} caName - Certificate Authority name
   * @returns {Promise<Object>} Imported certificate info
   */
  importCACertificate: async (csrId, certificatePem, caName) => {
    const response = await api.post('/certificates/import-ca-certificate', {
      csrId,
      certificatePem,
      caName
    });
    return response.data;
  },

  /**
   * Import an existing keypair (Model 3 — bring your own signing key).
   * @param {Object} request - { alias, format: 'PKCS12'|'PEM', activate,
   *   p12Base64?, p12Password?, keyAlias?, certificatePem?, privateKeyPem? }
   * @returns {Promise<Object>} Imported certificate info
   */
  importKeyPair: async (request) => {
    const response = await api.post('/certificates/import-keypair', request);
    return response.data;
  },

  /**
   * Activate certificate (make it primary)
   * @param {number} id - Certificate ID
   * @returns {Promise<void>}
   */
  activateCertificate: async (id) => {
    const response = await api.post(`/certificates/${id}/activate`);
    return response.data;
  },

  /**
   * Export the certificate and its private key as a passphrase-protected
   * PKCS#12 keystore (admin only). Errors come back as a Blob because of the
   * binary response type, so the server's message is read out of it here.
   * @param {number} id - Certificate ID
   * @param {string} passphrase - Passphrase that will protect the .p12 file
   * @returns {Promise<{blob: Blob, alias: string}>} Keystore and its entry alias
   */
  exportKeystore: async (id, passphrase) => {
    try {
      const response = await api.post(
        `/certificates/${id}/export-keystore`,
        { passphrase },
        { responseType: 'blob' }
      );
      return { blob: response.data, alias: response.headers['x-keystore-alias'] || 'signing' };
    } catch (err) {
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const body = JSON.parse(await data.text());
          err.message = body.error || body.message || err.message;
        } catch {
          // Not JSON — keep axios's own message.
        }
      }
      throw err;
    }
  },

  /**
   * Revoke certificate
   * @param {number} id - Certificate ID
   * @param {string} reason - Revocation reason
   * @returns {Promise<void>}
   */
  revokeCertificate: async (id, reason) => {
    const response = await api.post(`/certificates/${id}/revoke`, { reason });
    return response.data;
  },

  /**
   * Get CSR details by ID
   * @param {number} csrId - CSR ID
   * @returns {Promise<Object>} CSR details
   */
  getCSRById: async (csrId) => {
    const response = await api.get(`/certificates/csrs/${csrId}`);
    return response.data;
  },

  /**
   * List CSRs with optional status filter
   * @param {string} status - Status filter (PENDING, SIGNED, REJECTED, EXPIRED)
   * @returns {Promise<Array>} List of CSRs
   */
  listCSRs: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/certificates/csrs', { params });
    return response.data;
  }
};

/**
 * Switch Endpoint Configuration API Service
 */
export const switchEndpointApi = {
  /**
   * Get all switch endpoint configurations
   * @returns {Promise<Array>} List of all endpoint configurations
   */
  getAllEndpoints: async () => {
    const response = await api.get('/admin/switch-endpoints');
    return response.data;
  },

  /**
   * Get active switch endpoint configurations
   * @returns {Promise<Array>} List of active endpoint configurations
   */
  getActiveEndpoints: async () => {
    const response = await api.get('/admin/switch-endpoints/active');
    return response.data;
  },

  /**
   * Get endpoint configuration by ID
   * @param {number} id - Endpoint configuration ID
   * @returns {Promise<Object>} Endpoint configuration
   */
  getEndpointById: async (id) => {
    const response = await api.get(`/admin/switch-endpoints/${id}`);
    return response.data;
  },

  /**
   * Get endpoint configuration by message type
   * @param {string} messageType - Message type (e.g., 'pacs.008')
   * @returns {Promise<Object>} Endpoint configuration
   */
  getEndpointByMessageType: async (messageType) => {
    const response = await api.get(`/admin/switch-endpoints/message-type/${messageType}`);
    return response.data;
  },

  /**
   * Create new endpoint configuration
   * @param {Object} endpointData - Endpoint configuration data
   * @param {string} endpointData.messageType - Message type
   * @param {string} endpointData.endpointUrl - Endpoint URL
   * @param {string} endpointData.description - Description
   * @param {boolean} endpointData.isActive - Active status
   * @param {number} endpointData.connectTimeoutSeconds - Connection timeout (optional)
   * @param {number} endpointData.requestTimeoutSeconds - Request timeout (optional)
   * @param {string} username - Username for audit trail
   * @returns {Promise<Object>} Created endpoint configuration
   */
  createEndpoint: async (endpointData, username) => {
    const response = await api.post('/admin/switch-endpoints', endpointData, {
      headers: {
        'X-Username': username
      }
    });
    return response.data;
  },

  /**
   * Update endpoint configuration
   * @param {number} id - Endpoint configuration ID
   * @param {Object} updates - Updates to apply
   * @param {string} username - Username for audit trail
   * @returns {Promise<Object>} Updated endpoint configuration
   */
  updateEndpoint: async (id, updates, username) => {
    const response = await api.put(`/admin/switch-endpoints/${id}`, updates, {
      headers: {
        'X-Username': username
      }
    });
    return response.data;
  },

  /**
   * Toggle endpoint active status
   * @param {number} id - Endpoint configuration ID
   * @param {string} username - Username for audit trail
   * @returns {Promise<Object>} Updated endpoint configuration
   */
  toggleEndpointStatus: async (id, username) => {
    const response = await api.patch(`/admin/switch-endpoints/${id}/toggle`, null, {
      headers: {
        'X-Username': username
      }
    });
    return response.data;
  },

  /**
   * Delete endpoint configuration
   * @param {number} id - Endpoint configuration ID
   * @returns {Promise<void>}
   */
  deleteEndpoint: async (id) => {
    const response = await api.delete(`/admin/switch-endpoints/${id}`);
    return response.data;
  },

  /**
   * Clear endpoint configuration cache
   * @returns {Promise<Object>} Success message
   */
  clearCache: async () => {
    const response = await api.post('/admin/switch-endpoints/cache/clear');
    return response.data;
  },

  /**
   * Get default timeout configurations
   * @returns {Promise<Object>} Default timeout values
   */
  getDefaults: async () => {
    const response = await api.get('/admin/switch-endpoints/defaults');
    return response.data;
  }
};

/**
 * Message Exchange Ledger API Service — the CBS ↔ Switch message view.
 */
export const ledgerApi = {
  /**
   * List exchanged messages, newest first.
   * @param {Object} filters - { type, direction: 'TO_SWITCH'|'FROM_SWITCH', status, limit }
   */
  list: async (filters = {}) => {
    const params = {};
    if (filters.type)      params.type = filters.type;
    if (filters.direction) params.direction = filters.direction;
    if (filters.status)    params.status = filters.status;
    params.limit = filters.limit || 100;
    const response = await api.get('/ledger', { params });
    return response.data;
  },

  /** Supported message types in the ledger. */
  types: async () => {
    const response = await api.get('/ledger/types');
    return response.data;
  },

  /** Full detail for one message, including the raw signed XML. */
  detail: async (type, id) => {
    const response = await api.get(`/ledger/${type}/${id}`);
    return response.data;
  },

  /** Recent transaction traces (async request→callback chains), newest first. */
  traces: async (limit = 100) => {
    const response = await api.get('/ledger/traces', { params: { limit } });
    return response.data;
  },

  /** Ordered hop-by-hop timeline for one transaction, by correlation key. */
  trace: async (key) => {
    const response = await api.get('/ledger/trace', { params: { key } });
    return response.data;
  }
};

/**
 * Certification API Service — runs the AfricaNenda suite, returns a signed result.
 */
export const certificationApi = {
  /** Describe the certification suite (cases, categories, obligations). */
  getSuite: async () => {
    const response = await api.get('/certification/suite');
    return response.data;
  },
  /** Execute a certification run; returns the schema-valid signed result. */
  run: async (request) => {
    const response = await api.post('/certification/run', request);
    return response.data;
  },
  /**
   * Fetch the live, persisted participant-driven run for the authenticated tenant.
   * Cases the participant hasn't exercised yet come back as NOT_RUN.
   */
  progress: async () => {
    const response = await api.get('/certification/progress');
    return response.data;
  },
  /** Issue the signed report and close the active run (a fresh attempt starts after). */
  finalize: async () => {
    const response = await api.post('/certification/finalize');
    return response.data;
  },
  /** The last issued (finalized) report — keeps a finalized run viewable after reset (B3). */
  latestReport: async () => {
    const response = await api.get('/certification/report/latest');
    return response.status === 204 ? null : response.data;
  },
  /**
   * Download the certification instruction pack (Postman collection, Markdown +
   * PDF guides, and tenant-signed sample bodies) as a .zip.
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  downloadInstructions: async () => {
    // The pack names the API base the participant calls: this Sandbox's, not a local port.
    const response = await api.get('/certification/instructions', {
      responseType: 'blob', params: { base: participantApiBase('ISO20022') },
    });
    const cd = response.headers['content-disposition'] || '';
    const m = cd.match(/filename="?([^"]+)"?/);
    return { blob: response.data, filename: m ? m[1] : 'africanenda-certification-pack.zip' };
  },
  /**
   * How to run one certification case: request(s) with a tenant-signed sample
   * body, expected result, and scenario notes.
   * @param {string} caseId - e.g. 'ISO-CT-001'
   * @returns {Promise<{tenantSigned: boolean, signingNote: string, instruction: Object}>}
   */
  caseInstruction: async (caseId) => {
    const response = await api.get(`/certification/instructions/case/${caseId}`);
    return response.data;
  },
  /**
   * Download the issued certificate PDF — available once the CoE authorising
   * officer has approved the latest finalized run (409 until then).
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  downloadCertificate: async () => {
    const response = await api.get('/certification/certificate', { responseType: 'blob' });
    const m = (response.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: response.data, filename: m ? m[1] : 'certificate.pdf' };
  },

  /**
   * Start the automated run: the engine issues every certifying exchange on the
   * participant's behalf and finalizes. 403 unless the account is enabled for it.
   * @returns {Promise<Object>} the initial run status
   */
  startAutoRun: async () => (await api.post('/certification/auto-run')).data,

  /**
   * Live progress of the automated run. Also the permission probe the page uses:
   * a 403 here is what says this account may not launch one.
   */
  autoRunStatus: async () => (await api.get('/certification/auto-run/status')).data,
};

/**
 * Conformance Certification API Service
 */
export const conformanceApi = {
  /**
   * List the conformance suite (cases grouped by category, no execution).
   * @returns {Promise<Object>} { categories, total, cases }
   */
  getSuite: async () => {
    const response = await api.get('/conformance/suite');
    return response.data;
  },

  /**
   * Run the conformance suite, optionally scoped to one category.
   * @param {string} [category] - Category name; omit to run the full suite.
   * @returns {Promise<Object>} Scored ConformanceReport
   */
  run: async (category) => {
    const response = await api.post('/conformance/run', null, {
      params: category ? { category } : {}
    });
    return response.data;
  }
};

/**
 * Participant Callback Endpoints API Service — the URLs the switch calls back
 * into the participant's own systems (async pacs.002, inbound camt.056 recalls,
 * acmt account notifications, pain.013 RtP presentment).
 */
export const participantEndpointsApi = {
  /**
   * The callback catalog merged with this tenant's saved configuration.
   * @returns {Promise<Array>} [{ key, name, description, messageTypes, url,
   *                              authHeaderName, authHeaderValue, enabled,
   *                              configured, updatedAt }]
   */
  list: async () => {
    const response = await api.get('/participant/endpoints');
    return response.data;
  },

  /**
   * Save one endpoint's configuration. A blank URL clears the endpoint.
   * @param {string} key - Callback key (e.g. 'payment-status')
   * @param {Object} config - { url, authHeaderName, authHeaderValue, enabled }
   * @returns {Promise<Object>} The saved endpoint view
   */
  save: async (key, config) => {
    const response = await api.put(`/participant/endpoints/${key}`, config);
    return response.data;
  },

  /**
   * Send a switch-signed test callback to the configured URL.
   * @param {string} key - Callback key
   * @returns {Promise<Object>} { success, httpStatus, durationMs, detail, messageType, url }
   */
  test: async (key) => {
    const response = await api.post(`/participant/endpoints/${key}/test`);
    return response.data;
  }
};

export default api;
